/**
 * Activity Feed Component
 * Displays recent activities in a timeline format
 */
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  QuestionAnswer as QueryIcon,
  BugReport as TicketIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { activityApi, Activity } from '@/services/api';
import { formatDistanceToNow } from 'date-fns';

const activityIcons: Record<string, React.ReactNode> = {
  team_created: <AddIcon sx={{ color: '#6366F1' }} />,
  team_approved: <ApproveIcon sx={{ color: '#10B981' }} />,
  team_rejected: <RejectIcon sx={{ color: '#EF4444' }} />,
  team_updated: <SettingsIcon sx={{ color: '#3B82F6' }} />,
  member_added: <PersonIcon sx={{ color: '#6366F1' }} />,
  member_removed: <PersonIcon sx={{ color: '#9CA3AF' }} />,
  config_updated: <SettingsIcon sx={{ color: '#3B82F6' }} />,
  query_answered: <QueryIcon sx={{ color: '#6366F1' }} />,
  ticket_created: <TicketIcon sx={{ color: '#F59E0B' }} />,
  escalation: <TicketIcon sx={{ color: '#EF4444' }} />,
  admin_action: <SettingsIcon sx={{ color: '#8B5CF6' }} />,
  system_event: <InfoIcon sx={{ color: '#9CA3AF' }} />,
};

interface ActivityFeedProps {
  teamId?: string;
  limit?: number;
  title?: string;
  embedded?: boolean;
}

export default function ActivityFeed({ teamId, limit = 10, title = 'Recent Activity', embedded = false }: ActivityFeedProps) {
  const navigate = useNavigate();
  const { data: activities, isLoading } = useQuery({
    queryKey: ['activities', teamId, limit],
    queryFn: () =>
      teamId
        ? activityApi.getTeamActivities(teamId, { limit }).then((res) => res.data.items.slice(0, limit))
        : activityApi.getRecent({ limit }).then((res) => res.data.slice(0, limit)),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const loadingSkeleton = (
    <>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      {[1, 2, 3, 4, 5].map((i) => (
        <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1 }} />
      ))}
    </>
  );

  if (isLoading && embedded) {
    return <Box>{loadingSkeleton}</Box>;
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent>{loadingSkeleton}</CardContent>
      </Card>
    );
  }

  const content = (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: embedded ? 1 : 2 }}>
        <Typography variant={embedded ? 'subtitle1' : 'h6'} sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {activities && activities.length >= limit && teamId && (
          <Typography
            variant="caption"
            sx={{ color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            onClick={() => navigate(`/teams/${teamId}/analytics`)}
          >
            View all
          </Typography>
        )}
      </Box>
      {activities && activities.length > 0 ? (
        <List dense disablePadding sx={{ flex: 1, overflow: 'auto' }}>
          {activities.map((activity: Activity) => (
            <ListItem
              key={activity.id}
              sx={{
                px: 0,
                py: embedded ? 1.25 : 1.5,
                borderBottom: '1px solid',
                borderColor: '#F3F4F6',
                '&:last-child': { borderBottom: 'none' },
                alignItems: embedded ? 'center' : 'flex-start',
              }}
            >
              <ListItemIcon sx={{ minWidth: embedded ? 32 : 40, mt: embedded ? 0 : 0.5 }}>
                <Box
                  sx={{
                    width: embedded ? 24 : 32,
                    height: embedded ? 24 : 32,
                    borderRadius: embedded ? 1 : 1.5,
                    backgroundColor: '#F3F4F6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '& svg': { fontSize: embedded ? 14 : 18 },
                  }}
                >
                  {activityIcons[activity.activity_type] || <InfoIcon sx={{ color: '#9CA3AF' }} />}
                </Box>
              </ListItemIcon>
              <ListItemText
                primary={
                  embedded ? (
                    <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.4 }}>
                      {activity.title}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', ml: 1.5 }}>
                        {activity.actor_name || activity.actor_email || 'System'}
                        {' \u00b7 '}
                        {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                      </Typography>
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.4 }}>
                      {activity.title}
                    </Typography>
                  )
                }
                secondary={
                  embedded ? null : (
                    <Box>
                      {activity.description && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: 'block',
                            mb: 0.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '100%',
                          }}
                        >
                          {activity.description}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {activity.actor_name || activity.actor_email || 'System'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                        </Typography>
                      </Box>
                    </Box>
                  )
                }
              />
            </ListItem>
          ))}
        </List>
      ) : (
        <Box sx={{ py: embedded ? 2 : 4, textAlign: 'center', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="text.secondary" variant={embedded ? 'body2' : 'body1'}>No recent activity</Typography>
        </Box>
      )}
    </>
  );

  if (embedded) {
    return <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>{content}</Box>;
  }

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {content}
      </CardContent>
    </Card>
  );
}
